import { omit, isEqual, pickBy } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import store2 from 'store2';
import { SortColumn, SortDirections, updateSortColumns } from '../../utils/sorting';

type MinimalSearchAttributes = {
    [k: string]: unknown;
    page: number;
    sortColumns: SortColumn[];
};

const getInitialSearchParameters = () => {
    if (!location.search) {
        return {};
    }

    const result = Object.fromEntries(new URLSearchParams(location.search)) as unknown as MinimalSearchAttributes;
    if (result.page !== undefined) {
        result.page = Number(result.page);
    }
    return result;
};

function toQueryObject<Attributes extends { page: number; limit?: string; offset?: string }>(
    itemsPerPage: number,
    searchParameters: Attributes,
): { limit: string; offset: string } & Omit<Attributes, 'page' | 'limit' | 'offset'> {
    const limit = itemsPerPage.toString();
    const offset = (itemsPerPage * (searchParameters.page - 1)).toString();

    return { limit, offset, ...omit(searchParameters, ['page', 'limit', 'offset']) };
}

export function useSearch<Attributes>(
    defaultAttributes: Attributes,
    defaultSortColumns: SortColumn[] = [],
    itemsPerPage = 50,
    queryStoreKey?: string,
) {
    const defaultSearchParameters = useMemo(
        () => ({ page: 1, sortColumns: defaultSortColumns, ...defaultAttributes }),
        [defaultAttributes, defaultSortColumns],
    );
    const initialSearchParams = getInitialSearchParameters();
    const startSearchParams = { ...defaultSearchParameters, ...initialSearchParams };
    const [searchParameters, setSearchParameters] = React.useState<typeof startSearchParams>(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        store2.has(queryStoreKey) ? store2.get(queryStoreKey) : startSearchParams,
    );

    const [queryObject, setQueryObject] = React.useState(toQueryObject<typeof searchParameters>(itemsPerPage, searchParameters));

    useEffect(() => {
        setQueryObject(toQueryObject(itemsPerPage, searchParameters));
        if (queryStoreKey !== undefined) {
            store2.set(queryStoreKey, searchParameters);
        }
    }, [itemsPerPage, queryStoreKey, searchParameters]);

    const handleSortChanged = useCallback((columnName: string, direction: SortDirections | undefined) => {
        setSearchParameters((current) => ({
            ...current,
            page: 1,
            sortColumns: updateSortColumns(columnName, direction, current.sortColumns),
        }));
    }, []);

    const handleNavigation = useCallback((page: number) => {
        setSearchParameters((current) => ({ ...current, page }));
    }, []);

    const setSearchAttribute = useCallback((value: Partial<Attributes>) => {
        setSearchParameters((current) => {
            // Only update if the value is really different, ignoring undefined values
            const currentWithoutUndefined = pickBy(current, (v) => v !== undefined);
            const newWithoutUndefined = pickBy({ ...current, ...value }, (v) => v !== undefined);
            if (isEqual(currentWithoutUndefined, newWithoutUndefined)) {
                return current;
            }
            return { ...current, page: 1, ...value };
        });
    }, []);

    const resetSearch = useCallback(() => {
        setSearchParameters(defaultSearchParameters);
        if (queryStoreKey !== undefined) {
            store2.remove(queryStoreKey);
        }
    }, [defaultSearchParameters, queryStoreKey]);

    return { searchParameters, handleSortChanged, handleNavigation, setSearchAttribute, resetSearch, queryObject, itemsPerPage };
}
